export const sortByName = <T extends { name?: string | null }>(
  array: T[],
  options?: {
    sensitivity?: Intl.CollatorOptions['sensitivity']
    ignorePunctuation?: boolean
  },
): T[] => {
  const { sensitivity = 'base', ignorePunctuation = true } = options ?? {}
  return [...array].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '', undefined, {
      sensitivity,
      ignorePunctuation,
    }),
  )
}

export const uniqueByKey = <T>(array: T[], key: keyof T): T[] =>
  array.reduce(
    (x, y) => (x.findIndex(e => e[key] === y[key]) < 0 ? [...x, y] : x),
    [],
  )

export const subtract = <T>(a: T[], b: T[], key: keyof T): T[] => {
  const bSet = b.map(it => it[key])

  return a.filter(o1 => !bSet.includes(o1[key]))
}

export const range = n => Array.from({ length: n }, (value, key) => key)

export const groupBy = <T, K extends PropertyKey>(
  list: T[],
  getKey: (item: T) => K,
): Record<K, T[]> => {
  if (!list) {
    return {} as Record<K, T[]>
  }

  return list.reduce(
    (previous, currentItem) => {
      const group = getKey(currentItem)
      if (!previous[group]) {
        previous[group] = []
      }
      previous[group].push(currentItem)
      return previous
    },
    {} as Record<K, T[]>,
  )
}

/** `unknown` here stops T[K] satisfying the `Record<T[K], T>` key constraint below. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toRecord<T extends Record<string, any>, K extends keyof T>(
  array: T[],
  selector: K,
): Record<T[K], T> {
  if (!array) {
    return {} as Record<T[K], T>
  }

  return array?.reduce(
    (acc, item) => ({ ...acc, [item[selector]]: item }),
    {} as Record<T[K], T>,
  )
}

export const mergeByKey = <T>(a: T[], b: T[], key: keyof T) => {
  const ids = new Set(a.map(it => it[key]))
  return [...a, ...b.filter(it => !ids.has(it[key]))]
}

/**
 * Returns values from `array`. if element with same key exists in `second`, returns element from second instead
 */
export const pickIfExists = <T>(array: T[], second: T[], key: keyof T): T[] => {
  return array.map(it => second.find(sm => sm[key] === it[key]) || it)
}
